import { useState } from 'react';
import { Loader2, Search, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Confirm-Stepper Self-Heal (Diagnostics card): repairs matches stuck
// mid-confirm at any step (2 through 4), including the step-3/4 case that
// leaves an invoice "Reconciled" (matching_status="matched") but never
// payment-stamped (payment_status stays Unpaid) -- see routes/
// admin_confirm_stepper_self_heal.py's module docstring. Preview is
// read-only; Apply resumes each stuck match through confirm_match's own
// idempotent stepper -- never a second, hand-rolled completion path.
export function ConfirmStepperSelfHealPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setApplyResult(null);
    try {
      const { data } = await matchingAPI.previewConfirmStepperSelfHeal();
      setPreview(data);
      if (data.stuck_count > 0) {
        toast.warning(`${data.stuck_count} stuck pair(s) found`);
      } else {
        toast.success('No stuck confirm-stepper pairs found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await matchingAPI.applyConfirmStepperSelfHeal();
      setApplyResult(data);
      toast.success(`${data.healed ?? 0} healed of ${data.stuck_count ?? 0}`);
      const { data: refreshed } = await matchingAPI.previewConfirmStepperSelfHeal();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const pairs = preview?.pairs ?? [];
  const stuckCount = preview?.stuck_count ?? 0;
  const failed = applyResult?.failed ?? [];
  const skipped = applyResult?.skipped ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-500" />
          Confirm-Stepper Self-Heal
        </CardTitle>
        <CardDescription>
          Finds matches stuck mid-confirm: reconciled on the transaction side but never reaching
          matching_status="matched" on the invoice (stuck before step 3), or matched/partially_matched
          but never payment-stamped or finished (stuck at step 3 or 4 -- the "Reconciled but
          payment_status stays Unpaid" symptom). Apply resumes each one through confirm_match's
          own idempotent stepper and re-checks state afterward -- a resume that completes without
          error but doesn't actually change anything is reported skipped, never counted as healed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button data-testid="confirm-stepper-self-heal-preview" size="sm" variant="outline" onClick={runPreview} disabled={previewing}>
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            data-testid="confirm-stepper-self-heal-apply"
            size="sm"
            onClick={runApply}
            disabled={applying || !preview || stuckCount === 0}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
            Apply {stuckCount ? `${stuckCount} pair(s)` : ''}
          </Button>
        </div>

        {preview && (
          <div className="space-y-2" data-testid="confirm-stepper-self-heal-result">
            <p className="text-sm text-muted-foreground">{preview.message}</p>
            {pairs.length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="confirm-stepper-self-heal-pairs-empty">
                No stuck pairs found.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg" data-testid="confirm-stepper-self-heal-pairs">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Transaction ID</th>
                      <th className="text-left px-3 py-2 font-medium">Invoice ID</th>
                      <th className="text-left px-3 py-2 font-medium">Match ID</th>
                      <th className="text-left px-3 py-2 font-medium">Confirm Step</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.map((p) => (
                      <tr key={p.match_id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{p.transaction_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{p.invoice_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{p.match_id}</td>
                        <td className="px-3 py-2 text-xs">{p.confirm_step ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-2" data-testid="confirm-stepper-self-heal-apply-result">
            <p className="text-xs text-muted-foreground">
              Last apply: {applyResult.healed ?? 0} healed of {applyResult.stuck_count ?? 0}
              {failed.length > 0 && `, ${failed.length} failed`}
              {skipped.length > 0 && `, ${skipped.length} skipped`}
            </p>
            {failed.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                  Failed on apply -- not silently skipped, see reason below.
                </p>
                <div className="overflow-x-auto border rounded-lg border-red-200 dark:border-red-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Match ID</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failed.map((f) => (
                        <tr key={f.match_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{f.match_id}</td>
                          <td className="px-3 py-2 text-xs">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {skipped.length > 0 && (
              <div className="space-y-1" data-testid="confirm-stepper-self-heal-skipped">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  Resumed without error but state was unchanged -- not counted as healed.
                </p>
                <div className="overflow-x-auto border rounded-lg border-amber-200 dark:border-amber-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Match ID</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skipped.map((s) => (
                        <tr key={s.match_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{s.match_id}</td>
                          <td className="px-3 py-2 text-xs">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
